"use client";

import { CcResponsivePoster } from "@/components/services/shared/CcResponsivePoster";
import Link from "@/components/ui/CcLink";
import { ArrowUpRight } from "lucide-react";
import { useRef, useState } from "react";
import type { WebdesignPageCopy, WebdesignProjectPreview } from "./webdesign-page-copy";
import styles from "./webdesign-studio.module.scss";

export function CcWebdesignShowcase({
  copy,
  projects,
}: {
  copy: WebdesignPageCopy["hero"];
  projects: WebdesignProjectPreview[];
}) {
  const [active, setActive] = useState(0);
  const [requested, setRequested] = useState(0);
  const [mounted, setMounted] = useState(() => new Set([0]));
  const requestedRef = useRef(0);
  const loaded = useRef(new Set<number>());
  const select = (index: number) => {
    requestedRef.current = index;
    setRequested(index);
    setMounted(previous => new Set([...previous, index]));
    if (loaded.current.has(index)) setActive(index);
  };
  if (projects.length === 0) return null;

  return (
    <div className={styles.showcase}>
      <div className={styles.showcaseTop}>
        <span>
          <i />
          {copy.previewLabel}
        </span>
        <div className={styles.projectSelector} role="group" aria-label={copy.selectorLabel}>
          {projects.map((item, index) => (
            <button
              type="button"
              key={item.href}
              aria-pressed={requested === index}
              aria-controls="webdesign-client-preview"
              onClick={() => select(index)}
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>
      <div id="webdesign-client-preview" className={styles.clientPreview}>
        <div className={styles.browserBar} aria-hidden="true">
          <span>
            <i />
            <i />
            <i />
          </span>
          <span className={styles.browserTitle}>
            {projects.map((item, index) => (
              <span key={item.href} data-active={active === index}>
                {item.title}
              </span>
            ))}
          </span>
          <ArrowUpRight size={14} />
        </div>
        <div className={styles.clientImage}>
          {projects.map((item, index) => {
            const isActive = active === index;
            return (
              <div
                key={item.href}
                className={styles.clientScene}
                data-active={isActive}
                aria-hidden={!isActive}
              >
                {mounted.has(index) && <CcResponsivePoster
                  src={item.image}
                  alt={item.alt}
                  fill
                  sizes="(max-width: 760px) calc(100vw - 76px), 1100px"
                  first={index === requested}
                  onLoad={() => {
                    loaded.current.add(index);
                    if (requestedRef.current === index) setActive(index);
                  }}
                />}
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.showcaseBottom}>
        <div className={styles.showcaseBottomCopies}>
          {projects.map((item, index) => {
            const isActive = active === index;
            return (
              <div
                key={item.href}
                className={styles.showcaseBottomCopy}
                data-active={isActive}
                aria-hidden={!isActive}
                inert={!isActive ? true : undefined}
              >
                <span>{item.description}</span>
                <Link href={item.href}>
                  {copy.caseLabel}
                  <ArrowUpRight size={18} />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.stamp} aria-hidden="true">
        <span>✳</span>
        {copy.stamp}
      </div>
    </div>
  );
}
